import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'
import { mapFeedback } from '@/lib/mappers'
import type { FeedbackMessage, FeedbackType } from '@/lib/types'

/** Same cap as the users list: enough for a live listener, replaced by paging if it ever grows. */
export const FEEDBACK_PAGE_LIMIT = 300

/**
 * Live list of one kind of message. Filtered by `type` only and sorted in memory — exactly like the
 * Android app: an `orderBy('createdAt')` next to the `where('type')` filter would require a
 * composite Firestore index, which cannot be created from code on the Spark plan workflow.
 *
 * A message whose server timestamp has not arrived yet (`createdAt == null`) is the freshest one, so
 * it sorts first instead of dropping to the bottom.
 */
export function subscribeToFeedback(
  type: FeedbackType,
  onData: (items: FeedbackMessage[]) => void,
  onError: (error: Error) => void,
): () => void {
  const feedbackQuery = query(
    collection(getDbOrThrow(), 'feedback'),
    where('type', '==', type),
    limit(FEEDBACK_PAGE_LIMIT),
  )
  return onSnapshot(
    feedbackQuery,
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => mapFeedback(item.id, item.data()))
        .sort((left, right) => {
          if (left.createdAt === null) return -1
          if (right.createdAt === null) return 1
          return right.createdAt - left.createdAt
        })
      onData(items)
    },
    onError,
  )
}

/**
 * Number of unreviewed messages of both kinds, for the sidebar badge. A single-field equality filter
 * needs no index. Errors are swallowed: a badge must never break the shell.
 */
export function subscribeToNewFeedbackCount(onData: (count: number) => void): () => void {
  const newQuery = query(
    collection(getDbOrThrow(), 'feedback'),
    where('status', '==', 'new'),
    limit(FEEDBACK_PAGE_LIMIT),
  )
  return onSnapshot(
    newQuery,
    (snapshot) => onData(snapshot.size),
    () => onData(0),
  )
}

/**
 * Flip a message to "reviewed". `status`, `reviewedBy` and `reviewedAt` are the only fields written;
 * the rules require author, text and type to stay untouched. One-way on purpose — the same as in the
 * Android app.
 */
export function markFeedbackReviewed(id: string, reviewedBy: string): Promise<void> {
  return updateDoc(doc(getDbOrThrow(), 'feedback', id), {
    status: 'reviewed',
    reviewedBy,
    reviewedAt: serverTimestamp(),
  })
}
